import type { SettingsListActionRef } from "../../../components/SettingsListPage";
import { renderPaginationRange } from "../../../app/utils/paginationRange";
import { spacing, typography as designTypography } from "../../../app/design-system";
import { BankOutlined,DeleteOutlined,StopOutlined } from "@ant-design/icons";
import type { ListColumn } from "../../../../../../../shared/frontend/list/listColumn";
import { useQuery } from "@tanstack/react-query";
import { Alert,Button,Flex,Typography } from "../../../../../../../shared/frontend/ui";
import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import React from "react";
import type { ApiClient } from "../../../app/api/client";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatMoney } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { SettingsListPage,type ProColumnsWithMeasure } from "../../../components/SettingsListPage";
import { createPersonPaymentAllocationColumns,createPersonPaymentColumns,PERSON_PAYMENT_COLUMN_DEFINITIONS } from "./personPaymentColumns";
import type { PaymentRow,PersonPaymentFormValues } from "./personDetailTypes";
import { paymentAllocations } from "./personPaymentDisplay";
import { canSubmitBankOrder, transferOrderResolutionOptions } from "../../../app/api/bankOrders";
import { RefundTransferDialogs, type RefundTransferTask } from "../../finance/RefundTransferDialogs";
import { parseDateOnly } from "./personDetailUtils";
import { PERSON_DETAIL_PAYMENTS_GC_TIME_MS, PERSON_DETAIL_PAYMENTS_STALE_TIME_MS, personDetailQueryKeys } from "./personDetailQueryKeys";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

const PAYMENT_ROW_KIND_DEBT_COLLECTION = "debtCollection";
const DUE_DATE_COLUMN_KEY = "dueDate";

type Props = {
  api: Pick<ApiClient, "get">;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  personId: string;
  canWriteFinance: boolean;
  paymentsRef: React.RefObject<SettingsListActionRef | null>;
  paymentMethodNameByType: Record<number, string>;
  selectedRowKeys: React.Key[];
  setSelectedRowKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
  selectedRows: PaymentRow[];
  setSelectedRows: React.Dispatch<React.SetStateAction<PaymentRow[]>>;
  setPaymentEdit: (value: PaymentRow | null) => void;
  paymentEditForm: FormInstance<PersonPaymentFormValues>;
  setPaymentEditOpen: (value: boolean) => void;
  setPaymentCancellationDrawerOpen: (value: boolean) => void;
  setPaymentCancellationDrawerPayments: (value: PaymentRow[] | null) => void;
};

export function PersonPaymentsTab({
  api,
  text,
  units,
  personId,
  canWriteFinance,
  paymentsRef,
  paymentMethodNameByType,
  selectedRowKeys,
  setSelectedRowKeys,
  selectedRows,
  setSelectedRows,
  setPaymentEdit,
  paymentEditForm,
  setPaymentEditOpen,
  setPaymentCancellationDrawerOpen,
  setPaymentCancellationDrawerPayments
}: Props) {
  const paymentsQuery = useQuery({
    queryKey: personDetailQueryKeys.person.payments(personId),
    queryFn: () => api.get<PaymentRow[]>(`/api/v1/persons/${encodeURIComponent(personId)}/payments?groupTransactions=true`),
    enabled: !!personId,
    retry: false,
    staleTime: PERSON_DETAIL_PAYMENTS_STALE_TIME_MS,
    gcTime: PERSON_DETAIL_PAYMENTS_GC_TIME_MS
  });
  const [refundTransfer, setRefundTransfer] = React.useState<RefundTransferTask | null>(null);
  const closeRefundTransfer = React.useCallback(() => setRefundTransfer(null), [setRefundTransfer]);
  const transactionRows = React.useMemo(() => (paymentsQuery.data ?? []).map((payment) => ({
    ...payment,
    key: payment.id,
    detailRows: payment.allocations ?? []
  })), [paymentsQuery.data]);
  const paymentsAllColumnsMap: Record<string, ListColumn<PaymentRow>> = React.useMemo(
    () => createPersonPaymentColumns({
      text,
      units,
      paymentMethodNameByType
    }),
    [paymentMethodNameByType, text, units]
  );

  const openPaymentEdit = React.useCallback(
    (payment: PaymentRow) => {
      setPaymentEdit(payment);
      paymentEditForm.setFieldsValue({
        amount: Number(payment.amount || 0),
        paidDate: parseDateOnly(String(payment.paidDate || "").slice(0, ISO_DATE_LENGTH)),
        method: payment.method || undefined,
        reference: payment.reference || undefined,
      });
      setPaymentEditOpen(true);
    },
    [paymentEditForm, setPaymentEdit, setPaymentEditOpen]
  );
  const allocationColumns = React.useMemo(
    () => createPersonPaymentAllocationColumns({ text, units }),
    [text, units]
  );



  const paymentActionsColumn: ProColumnsWithMeasure<PaymentRow> = {
    title: "",
    key: "actions",
    valueType: "option",
    fixed: "right" as const,
    align: "right" as const,
    render: (_: unknown, r: PaymentRow) => {
      if (!canWriteFinance) return null;
      if (r.rowKind === PAYMENT_ROW_KIND_DEBT_COLLECTION) return null;
      // Rückerstattung per Überweisung: senden oder klären, wie bisher auf der Seite „Stornierungen“.
      if (r.transferOrderId && r.transferOrderState != null) {
        const transferOrderId = r.transferOrderId;
        const state = r.transferOrderState;
        return [
          canSubmitBankOrder(state) ? (
            <Button key="transfer" type="link" size="small" icon={<BankOutlined />}
              onClick={(event) => { event.stopPropagation(); setRefundTransfer({ kind: "submit", transferOrderIds: [transferOrderId] }); }}>
              {text("Banking send to bank")}
            </Button>
          ) : null,
          transferOrderResolutionOptions(state).length > 0 ? (
            <Button key="resolve" type="link" size="small"
              onClick={(event) => { event.stopPropagation(); setRefundTransfer({ kind: "resolve", transferOrderId, state }); }}>
              {text("Banking resolve transfer")}
            </Button>
          ) : null
        ];
      }
      const isRev =
        r.isReversal === true || (!!r.reversesPaymentId && Number(r.amount || 0) < 0);
      if (isRev) return null;
      if (r.isCancelled && r.canVoid !== true) return null;
      const canVoidBtn = r.canVoid === true;
      // Bearbeiten öffnet der Zeilenklick.
      if (!canVoidBtn) return null;
      return [
        canVoidBtn ? (
          <Button
            key="void"
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              setPaymentCancellationDrawerPayments(paymentAllocations(r).filter((payment) => payment.canVoid === true));
              setPaymentCancellationDrawerOpen(true);
            }}
          >
            {text("Void")}
          </Button>
        ) : null,
      ];
    },
  };
  const allColumnsMap = { ...paymentsAllColumnsMap, actions: paymentActionsColumn };
  const columnDefinitions = [
    ...PERSON_PAYMENT_COLUMN_DEFINITIONS,
    ...(canWriteFinance ? [{ key: "actions", label: "Actions", defaultVisible: true }] : [])
  ];

  const requestPayments = React.useCallback(async () => ({ data: paymentsQuery.data ?? [], success: !paymentsQuery.isError, total: paymentsQuery.data?.length ?? 0 }), [paymentsQuery.data, paymentsQuery.isError]);

  return (
    <>
    <RefundTransferDialogs task={refundTransfer} onClose={closeRefundTransfer} onDone={() => { void paymentsQuery.refetch(); }} />
    <SettingsListPage<PaymentRow>
      toolbarRightExtra={canWriteFinance ? (
        <Button
          danger
          icon={<StopOutlined />}
          onClick={() => {
            const preSelected = (selectedRows || []).flatMap(paymentAllocations).filter((r) => r.canVoid === true);
            setPaymentCancellationDrawerPayments(preSelected.length > 0 ? preSelected : []);
            setPaymentCancellationDrawerOpen(true);
          }}
        >
          {text("Stornierung")}
        </Button>
      ) : undefined}
      storageKey="persons.detail.payments"
      rowKey="id"
      actionRef={paymentsRef}
      request={requestPayments}
      columnDefinitions={columnDefinitions}
      allColumnsMap={allColumnsMap}
      expandableGroup={{
        data: transactionRows,
        loading: paymentsQuery.isPending,
        summaryColumns: [
          ...Object.values(paymentsAllColumnsMap).filter((column) => column.key !== DUE_DATE_COLUMN_KEY),
          ...(canWriteFinance ? [paymentActionsColumn] : [])
        ],
        // Allocations explain the receipt; financial actions belong to its summary row.
        childColumnKeys: [],
        childColumns: allocationColumns,
        rowSelection: canWriteFinance ? {
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows as unknown as PaymentRow[]);
          },
          getCheckboxProps: (row) => ({ disabled: row.canVoid !== true }),
          hideSelectAll: false,
          selectOnRowClick: true
        } : undefined
      }}
      titleExtra={paymentsQuery.isError ? <Alert type="error" title={text("Payment transactions could not be loaded")} /> : undefined}
      onRowClick={(r) => {
        // Nicht änderbare Zahlungen öffnen gesperrt (Nur-Lesen), Inkassozeilen haben keine Ansicht.
        if (r.rowKind === PAYMENT_ROW_KIND_DEBT_COLLECTION) return;
        openPaymentEdit(r);
      }}
      pagination={{
        pageSize: 20,
        showTotal: (total: number, range: [number, number]) => {
          if (selectedRows.length === 0) {
            return renderPaginationRange(text, total, range);
          }
          const totalAmount = selectedRows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
          return (
            <Flex align="center" gap={spacing.lg} wrap="nowrap" justify="flex-start" style={{ width: "100%" }}>
              <Typography.Text type="secondary" style={{ fontSize: designTypography.fontSize.compact, whiteSpace: "nowrap" }}>
                {text("Sum (selected)")}: <Typography.Text strong style={{ fontSize: designTypography.fontSize.compact }}>{formatMoney(totalAmount, units)}</Typography.Text>
              </Typography.Text>
              <div style={{ flex: 1 }} />
              {renderPaginationRange(text, total, range)}
            </Flex>
          );
        },
      }}
    />
    </>
  );
}
